import { TpCatalogSync, TpExportData, TpDataIngestion, TpOrganizationInfo } from '../../../api/models/admin';
import { M2MApi, AdminApi } from '../../../api/models/m2mApi';
import { TaskExecutionType } from '../../../api/models/tasks';
import { FORM_DATATYPES, promptAutoForm, TpAutoFormDef } from '../../../components/auto-form-modal/AutoForm';
import { TpUserInfo } from '../../../data/interfaces';

export function promptCreateOrganization(m2mApi: M2MApi): Promise<TpOrganizationInfo> {
  const form: TpAutoFormDef = {
    title: `Create new Organization`,
    fields: [
      {
        id: 'name',
        name: 'Organization Name',
        dataType: FORM_DATATYPES.STRING,
        startContent: '',
        inputValidation: (content) => {
          return /^[a-z0-9-]+$/.test(content);
        },
        ValidationMessage: 'Only lowercase characters, numbers and hyphens are allowed.',
        required: true,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const newOrganization: TpOrganizationInfo = {
      name: dataForm.name,
      isActive: true,
    };
    return m2mApi.systemAdmin.createOrganization(newOrganization).then((resp) => {
      return resp;
    });
  });
}

/**
 * Ask for confirmation to delete the organization.
 * Include warnings that organization data should be cleaned up first and users should first be removed from the organization.
 * Also include a checkbox to delete the associated bucket (if any).
 * @param m2mApi
 * @param organization
 */
export function promptDeleteOrganization(m2mApi: M2MApi, organization: TpOrganizationInfo): Promise<null | string> {
  const organizationId = organization.identifier;
  if (!organizationId) {
    return Promise.reject('Organization identifier is missing');
  }

  const form: TpAutoFormDef = {
    title: `Delete Organization ${organization.name}`,
    description: `WARNING: Deleting an organization is irreversible.\n\nThis action can only be performed if all data has been cleaned up and all users have been removed from the organization.\n\nPlease confirm if you want to proceed with deleting the organization.`,
    fields: [
      {
        id: 'bucket_delete',
        name: 'Delete associated bucket (if any)',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    return m2mApi.systemAdmin.deleteOrganization(organizationId, dataForm.bucket_delete).then(() => null);
  });
}

export function promptCleanUpOrganization(
  adminApi: AdminApi,
  organization: TpOrganizationInfo,
  user: TpUserInfo,
  tags: string[],
): Promise<TaskExecutionType> {
  const inputTags = ['*'].concat(tags);
  const form: TpAutoFormDef = {
    title: `Cleanup organization ${organization.name}`,
    fields: [
      {
        id: 'tag',
        name: 'Ingestion Tag (* for all)',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        choices: inputTags.map((tag) => ({ id: tag, content: tag })),
        startContent: '*',
        required: true,
      },
      {
        id: 'cleanTemplates',
        name: 'Clean Templates (only orphaned)',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'dryRun',
        name: 'Dry Run',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: true,
        required: false,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    return adminApi.cleanupOrganization(user, organization, {
      cleanTemplates: dataForm.cleanTemplates,
      ingestionTag: dataForm.tag,
      dryRun: dataForm.dryRun,
    });
  });
}

export function promptIngestData(
  adminApi: AdminApi,
  organization: TpOrganizationInfo,
  user: TpUserInfo,
): Promise<TaskExecutionType> {
  const form: TpAutoFormDef = {
    title: `Ingest data for ${organization.name}`,
    fields: [
      {
        id: 'bucket_path',
        name: 'Bucket Path',
        dataType: FORM_DATATYPES.STRING,
        startContent: `gs://${organization.organizationPrefix}/`,
        inputValidation: (content) => {
          return /^gs:\/\//.test(content);
        },
        ValidationMessage: 'Only Google buckets in the format gs://bucket-name are allowed.',
        required: true,
      },
      {
        id: 'templates_only',
        name: 'Templates Only',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'data_only',
        name: 'Data Only',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'skip_data_validation',
        name: 'Skip Data Validation',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'skip_failed_validations',
        name: 'Skip Failed Validations',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'skip_failed_transformations',
        name: 'Skip Failed Transformations',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'non_default_templates',
        name: 'Do not set new templates as default',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'allow_duplicate_ontologies',
        name: 'Allow duplicate ontologies',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'dry_run',
        name: 'Dry Run',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: true,
        required: false,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const ingestData: TpDataIngestion = {
      organizationName: organization.name,
      ownerEmail: user.email!,
      bucketPath: dataForm.bucket_path,
      templatesOnly: dataForm.templates_only,
      dataOnly: dataForm.data_only,
      allowDuplicateOntologies: dataForm.allow_duplicate_ontologies,
      skipDataValidation: dataForm.skip_data_validation,
      skipFailedValidations: dataForm.skip_failed_validations,
      skipFailedTransformations: dataForm.skip_failed_transformations,
      nonDefaultTemplates: dataForm.non_default_templates,
      dryRun: dataForm.dry_run,
    };
    return adminApi.ingestData(ingestData).then((resp) => {
      return resp;
    });
  });
}

export function promptSyncCatalogAsset(
  adminApi: AdminApi,
  organization: TpOrganizationInfo,
  user: TpUserInfo,
): Promise<TaskExecutionType> {
  const form: TpAutoFormDef = {
    title: `Sync catalog asset for ${organization.name}`,
    fields: [
      {
        id: 'delete_previous_catalog_assets',
        name: 'Delete Previous Catalog Assets',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: true,
        required: false,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const catalogSyncData: TpCatalogSync = {
      organizationName: organization.name,
      userEmail: user.email!,
      deletePreviousCatalogAssets: dataForm.delete_previous_catalog_assets,
    };

    return adminApi.syncCatalogAsset(catalogSyncData).then((resp) => {
      return resp;
    });
  });
}

export function promptExportData(
  adminApi: AdminApi,
  organization: TpOrganizationInfo,
  user: TpUserInfo,
  tags: string[],
): Promise<TaskExecutionType> {
  const inputTags = ['*'].concat(tags);
  const form: TpAutoFormDef = {
    title: `Export data for ${organization.name}`,
    fields: [
      {
        id: 'tag',
        name: 'Ingestion Tag (* for all)',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        choices: inputTags.map((tag) => ({ id: tag, content: tag })),
        startContent: '*',
        required: true,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const exportData: TpExportData = {
      organizationName: organization.name,
      userEmail: user.email!,
      ingestionTag: dataForm.tag,
    };

    return adminApi.exportData(exportData).then((resp) => {
      return resp;
    });
  });
}
